import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Input,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
} from '@mlm/ui';
import {
  BASELINE_THEME_TOKENS,
  LAYOUT_DEFAULT_OMNI,
  SECTION_LIBRARY,
  createSection,
  deriveTheme,
  hexToHslTriplet,
  hslTripletToHex,
  type StorefrontLayout,
  type StorefrontSection,
  type StorefrontSectionType,
  type ThemeTokens,
} from '@mlm/shared';
import {
  getTenantFeatures,
  getTenantTheme,
  patchTenantFeatures,
  patchTenantTheme,
} from '../lib/api';
import { useIsSuperAdmin } from '../lib/auth-store';
import { ConfirmSaveButton } from '../components/confirm-save-button';
import { LogoUploadField } from '../components/logo-upload-field';

function clone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T;
}

type ColorKey = Extract<keyof ThemeTokens, string>;

const BRAND_COLORS: { key: ColorKey; label: string }[] = [
  { key: 'primary', label: 'Primary (brand)' },
  { key: 'primaryForeground', label: 'On primary' },
  { key: 'accent', label: 'Accent / tint' },
  { key: 'accentForeground', label: 'On accent' },
  { key: 'ring', label: 'Focus ring' },
  { key: 'brandDark', label: 'Brand dark (hover)' },
  { key: 'brandLight', label: 'Brand light' },
  { key: 'highlight', label: 'Highlight (gold)' },
  { key: 'pv', label: 'PV badge' },
  { key: 'pvBg', label: 'PV badge bg' },
  { key: 'pvBorder', label: 'PV badge border' },
];

const SURFACE_COLORS: { key: ColorKey; label: string }[] = [
  { key: 'background', label: 'Page background' },
  { key: 'foreground', label: 'Body text' },
  { key: 'card', label: 'Card' },
  { key: 'cardForeground', label: 'On card' },
  { key: 'secondary', label: 'Secondary' },
  { key: 'secondaryForeground', label: 'On secondary' },
  { key: 'muted', label: 'Muted' },
  { key: 'mutedForeground', label: 'Muted text' },
  { key: 'border', label: 'Border' },
  { key: 'input', label: 'Input border' },
  { key: 'success', label: 'Success' },
];

function ColorField({
  label,
  value,
  baseline,
  onChange,
}: {
  label: string;
  value: string | undefined;
  baseline?: string;
  onChange: (v: string | undefined) => void;
}) {
  const effective = value ?? baseline;
  const hex = effective ? hslTripletToHex(effective) : '#cccccc';
  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={hex}
        onChange={(e) => onChange(hexToHslTriplet(e.target.value))}
        className="h-9 w-10 shrink-0 cursor-pointer rounded border"
        aria-label={label}
        title={label}
      />
      <div className="min-w-0 flex-1">
        <div className="truncate text-xs font-medium">{label}</div>
        <Input
          value={value ?? ''}
          placeholder={baseline ? `${baseline} · baseline` : 'unset → baseline'}
          onChange={(e) => onChange(e.target.value.trim() === '' ? undefined : e.target.value)}
          className="mt-0.5 h-7 font-mono text-[11px]"
        />
      </div>
      {value !== undefined && (
        <button
          type="button"
          onClick={() => onChange(undefined)}
          className="shrink-0 text-[11px] text-muted-foreground hover:text-foreground"
          title="Reset to baseline"
        >
          reset
        </button>
      )}
    </div>
  );
}

function TextField({
  label,
  value,
  placeholder,
  onChange,
}: {
  label: string;
  value: string | undefined;
  placeholder?: string;
  onChange: (v: string | undefined) => void;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium">{label}</span>
      <Input
        value={value ?? ''}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value.trim() === '' ? undefined : e.target.value)}
        className="mt-0.5"
      />
    </label>
  );
}

export function LayoutThemePage() {
  const canWrite = useIsSuperAdmin();
  const qc = useQueryClient();
  const themeQ = useQuery({ queryKey: ['tenant-theme'], queryFn: getTenantTheme });
  const featQ = useQuery({ queryKey: ['tenant-features'], queryFn: getTenantFeatures });

  const [theme, setTheme] = useState<ThemeTokens | null>(null);
  const [layout, setLayout] = useState<StorefrontLayout | null>(null);
  const [brandInput, setBrandInput] = useState({ primary: '', secondary: '', highlight: '' });
  const [deriveErr, setDeriveErr] = useState<string | null>(null);
  // Per-page pending "Add section" type (keyed by page index).
  const [addType, setAddType] = useState<Record<number, StorefrontSectionType>>({});

  useEffect(() => {
    if (themeQ.data && !theme) setTheme(clone(themeQ.data));
  }, [themeQ.data, theme]);
  useEffect(() => {
    if (featQ.data && !layout) {
      setLayout(clone(featQ.data.storefront?.layout ?? LAYOUT_DEFAULT_OMNI));
    }
  }, [featQ.data, layout]);

  const saveTheme = useMutation({
    meta: { successMessage: 'Theme saved.' },
    mutationFn: (t: ThemeTokens) => patchTenantTheme(t),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant-theme'] }),
  });
  const saveLayout = useMutation({
    meta: { successMessage: 'Layout saved.' },
    mutationFn: (l: StorefrontLayout) =>
      patchTenantFeatures({ storefront: { ...featQ.data!.storefront, layout: l } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tenant-features'] });
      qc.invalidateQueries({ queryKey: ['storefront-content'] });
    },
  });

  const setT = (key: ColorKey, v: string | undefined) =>
    setTheme((d) => (d ? { ...d, [key]: v } : d));

  // Brand-asset upload persists immediately; fold only the asset fields into the
  // draft so a later "Save theme" (which merge-patches the whole draft) can't
  // clobber the just-uploaded key. URL edits / clears stay in the batch draft.
  const onLogoUploaded = (t: ThemeTokens) =>
    setTheme((d) =>
      d
        ? {
            ...d,
            logoKey: t.logoKey,
            logoKeyDark: t.logoKeyDark,
            faviconKey: t.faviconKey,
            logoUrl: t.logoUrl,
            logoUrlDark: t.logoUrlDark,
          }
        : d,
    );
  const onLogoField = (patch: Partial<ThemeTokens>) =>
    setTheme((d) => (d ? { ...d, ...patch } : d));

  // deriveTheme: 1-2 brand colors -> the full color family, merged into the
  // draft (foreground/hover-dark/tint/ring/gradient). Review + Save after.
  function deriveFromBrand() {
    setDeriveErr(null);
    try {
      const d = deriveTheme({
        primary: brandInput.primary,
        secondary: brandInput.secondary.trim() || undefined,
        highlight: brandInput.highlight.trim() || undefined,
      });
      setTheme((prev) => (prev ? { ...prev, ...d } : d));
    } catch (e) {
      setDeriveErr(e instanceof Error ? e.message : 'Invalid color — use #hex or "H S% L%".');
    }
  }

  function moveSection(pageIdx: number, secIdx: number, dir: -1 | 1) {
    setLayout((d) => {
      if (!d) return d;
      const next = clone(d);
      const secs = next.pages[pageIdx]!.sections;
      const j = secIdx + dir;
      if (j < 0 || j >= secs.length) return d;
      [secs[secIdx], secs[j]] = [secs[j]!, secs[secIdx]!];
      return next;
    });
  }
  function patchSection(pageIdx: number, secIdx: number, patch: Partial<StorefrontSection>) {
    setLayout((d) => {
      if (!d) return d;
      const next = clone(d);
      const secs = next.pages[pageIdx]!.sections;
      secs[secIdx] = { ...secs[secIdx]!, ...patch } as StorefrontSection;
      return next;
    });
  }
  function addSection(pageIdx: number, type: StorefrontSectionType) {
    setLayout((d) => {
      if (!d) return d;
      const next = clone(d);
      const secs = next.pages[pageIdx]!.sections;
      // Unique id across the WHOLE layout so React keys / anchors never collide.
      const allIds = next.pages.flatMap((p) => p.sections.map((s) => s.id));
      secs.push(createSection(type, allIds));
      return next;
    });
  }
  function removeSection(pageIdx: number, secIdx: number) {
    setLayout((d) => {
      if (!d) return d;
      const next = clone(d);
      next.pages[pageIdx]!.sections.splice(secIdx, 1);
      return next;
    });
  }

  if (themeQ.isLoading || featQ.isLoading || !theme || !layout) {
    return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">Layout &amp; Theme</h1>
        <p className="text-sm text-muted-foreground">
          Super-admin only. Colors/fonts/logo reskin the whole storefront; layout reorders the
          storefront's sections. Leave a color empty to fall back to the showroom baseline.
        </p>
      </div>

      <Tabs defaultValue="theme">
        <TabsList>
          <TabsTrigger value="theme">Theme</TabsTrigger>
          <TabsTrigger value="layout">Layout</TabsTrigger>
        </TabsList>

        <TabsContent value="theme" className="space-y-4">
          <div className="flex justify-end">
            <ConfirmSaveButton
              label="Save theme"
              title="Save theme tokens"
              current={themeQ.data}
              next={theme}
              loading={saveTheme.isPending}
              disabled={!canWrite}
              onConfirm={() => saveTheme.mutateAsync(theme)}
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Auto-fill from brand color</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Paste the client's primary brand color (hex or HSL triplet), and optionally a
                secondary + a highlight/gold. <strong>Derive</strong> fills the color tokens below
                (foreground, hover-dark, tint, ring, gradient); set fonts/logo below, then review
                and Save.
              </p>
              <div className="grid gap-3 sm:grid-cols-3">
                <TextField
                  label="Primary (hex)"
                  value={brandInput.primary || undefined}
                  placeholder="#43a047"
                  onChange={(v) => setBrandInput((b) => ({ ...b, primary: v ?? '' }))}
                />
                <TextField
                  label="Secondary (optional)"
                  value={brandInput.secondary || undefined}
                  placeholder="#1b2838"
                  onChange={(v) => setBrandInput((b) => ({ ...b, secondary: v ?? '' }))}
                />
                <TextField
                  label="Highlight / gold (optional)"
                  value={brandInput.highlight || undefined}
                  placeholder="#FAD11D"
                  onChange={(v) => setBrandInput((b) => ({ ...b, highlight: v ?? '' }))}
                />
              </div>
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="secondary"
                  disabled={!brandInput.primary.trim() || !canWrite}
                  onClick={deriveFromBrand}
                >
                  Derive &amp; fill colors
                </Button>
                {deriveErr && <span className="text-xs text-destructive">{deriveErr}</span>}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Brand colors</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {BRAND_COLORS.map((c) => (
                <ColorField
                  key={c.key}
                  label={c.label}
                  value={theme[c.key] as string | undefined}
                  baseline={BASELINE_THEME_TOKENS[c.key] as string | undefined}
                  onChange={(v) => setT(c.key, v)}
                />
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Surfaces (advanced)</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {SURFACE_COLORS.map((c) => (
                <ColorField
                  key={c.key}
                  label={c.label}
                  value={theme[c.key] as string | undefined}
                  baseline={BASELINE_THEME_TOKENS[c.key] as string | undefined}
                  onChange={(v) => setT(c.key, v)}
                />
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Typography &amp; brand assets</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3 sm:grid-cols-2">
              <TextField
                label="Heading font (CSS stack)"
                value={theme.fontHeading}
                placeholder="'Playfair Display', serif"
                onChange={(v) => setT('fontHeading', v)}
              />
              <TextField
                label="Body font (CSS stack)"
                value={theme.fontBody}
                placeholder="'Inter', system-ui, sans-serif"
                onChange={(v) => setT('fontBody', v)}
              />
              <label className="block sm:col-span-2">
                <span className="text-xs font-medium">Font stylesheet URLs (one per line)</span>
                <Textarea
                  value={(theme.fontLinks ?? []).join('\n')}
                  placeholder="https://fonts.googleapis.com/css2?family=Inter:wght@400;600&display=swap"
                  rows={2}
                  className="mt-0.5 font-mono text-xs"
                  onChange={(e) => {
                    const links = e.target.value
                      .split('\n')
                      .map((s) => s.trim())
                      .filter(Boolean);
                    setTheme((d) =>
                      d ? { ...d, fontLinks: links.length ? links : undefined } : d,
                    );
                  }}
                />
              </label>
              <div className="grid gap-4 sm:col-span-2 sm:grid-cols-3">
                <LogoUploadField
                  which="light"
                  label="Logo"
                  theme={theme}
                  disabled={!canWrite}
                  onUploaded={onLogoUploaded}
                  onFieldChange={onLogoField}
                />
                <LogoUploadField
                  which="dark"
                  label="Logo (dark surfaces)"
                  theme={theme}
                  disabled={!canWrite}
                  onUploaded={onLogoUploaded}
                  onFieldChange={onLogoField}
                />
                <LogoUploadField
                  which="favicon"
                  label="Favicon"
                  theme={theme}
                  disabled={!canWrite}
                  onUploaded={onLogoUploaded}
                  onFieldChange={onLogoField}
                />
              </div>
              <TextField
                label="Wordmark (text fallback)"
                value={theme.wordmark}
                placeholder="Your brand"
                onChange={(v) => setT('wordmark', v)}
              />
              <TextField
                label="Corner radius"
                value={theme.radius}
                placeholder="0.5rem"
                onChange={(v) => setT('radius', v)}
              />
              <TextField
                label="Brand gradient (CSS)"
                value={theme.brandGradient}
                placeholder="linear-gradient(120deg,#ff6633,#ee4d2d 55%,#d4380d)"
                onChange={(v) => setT('brandGradient', v)}
              />
              <TextField
                label="Member gradient (CSS)"
                value={theme.memberGradient}
                onChange={(v) => setT('memberGradient', v)}
              />
              <TextField
                label="Gold gradient (CSS)"
                value={theme.goldGradient}
                onChange={(v) => setT('goldGradient', v)}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="layout" className="space-y-4">
          <div className="flex justify-end">
            <ConfirmSaveButton
              label="Save layout"
              title="Save storefront layout"
              current={featQ.data?.storefront?.layout}
              next={layout}
              loading={saveLayout.isPending}
              disabled={!canWrite}
              onConfirm={() => saveLayout.mutateAsync(layout)}
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Reorder, show/hide, and retitle the storefront's sections. The home page served at{' '}
            <code>/</code> is the one whose path matches{' '}
            <code className="font-mono">{layout.homePath ?? '/storefront'}</code>.
          </p>

          {layout.pages.map((page, pi) => (
            <Card key={page.path}>
              <CardHeader>
                <CardTitle className="font-mono text-sm">{page.path}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {page.sections.map((s, si) => {
                  const cfg = s.config as Record<string, unknown>;
                  const hasTitle =
                    typeof cfg.title === 'string' ||
                    s.type === 'flashSale' ||
                    s.type === 'productFeed' ||
                    s.type === 'productRow';
                  return (
                    <div key={s.id} className="flex items-center gap-2 rounded border p-2">
                      <div className="flex flex-col">
                        <button
                          type="button"
                          className="text-xs leading-none text-muted-foreground hover:text-foreground disabled:opacity-30"
                          disabled={si === 0}
                          onClick={() => moveSection(pi, si, -1)}
                          title="Move up"
                        >
                          ▲
                        </button>
                        <button
                          type="button"
                          className="text-xs leading-none text-muted-foreground hover:text-foreground disabled:opacity-30"
                          disabled={si === page.sections.length - 1}
                          onClick={() => moveSection(pi, si, 1)}
                          title="Move down"
                        >
                          ▼
                        </button>
                      </div>
                      <Checkbox
                        checked={s.enabled}
                        onCheckedChange={(v) => patchSection(pi, si, { enabled: !!v })}
                        aria-label={`Enable ${s.type}`}
                      />
                      <span className="w-32 shrink-0 font-mono text-xs">{s.type}</span>
                      {hasTitle && (
                        <Input
                          value={(cfg.title as string | undefined) ?? ''}
                          placeholder="Section title (optional)"
                          className="h-8 text-xs"
                          onChange={(e) => {
                            const title = e.target.value.trim() === '' ? undefined : e.target.value;
                            patchSection(pi, si, {
                              config: { ...cfg, title },
                            } as Partial<StorefrontSection>);
                          }}
                        />
                      )}
                      <button
                        type="button"
                        className="ml-auto shrink-0 rounded px-2 py-1 text-xs text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:opacity-30"
                        disabled={!canWrite}
                        onClick={() => removeSection(pi, si)}
                        title="Remove section"
                        aria-label={`Remove ${s.type}`}
                      >
                        Remove
                      </button>
                    </div>
                  );
                })}
                {page.sections.length === 0 && (
                  <p className="px-1 py-2 text-xs text-muted-foreground">
                    No sections yet — add one below.
                  </p>
                )}
                <div className="flex items-center gap-2 border-t pt-2">
                  <select
                    className="h-8 rounded border bg-background px-2 text-xs"
                    value={addType[pi] ?? SECTION_LIBRARY[0]!.type}
                    disabled={!canWrite}
                    aria-label="Section type to add"
                    onChange={(e) =>
                      setAddType((m) => ({ ...m, [pi]: e.target.value as StorefrontSectionType }))
                    }
                  >
                    {SECTION_LIBRARY.map((e) => (
                      <option key={e.type} value={e.type}>
                        {e.label}
                      </option>
                    ))}
                  </select>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={!canWrite}
                    onClick={() => addSection(pi, addType[pi] ?? SECTION_LIBRARY[0]!.type)}
                  >
                    + Add section
                  </Button>
                  <span className="text-[11px] text-muted-foreground">
                    {
                      SECTION_LIBRARY.find(
                        (e) => e.type === (addType[pi] ?? SECTION_LIBRARY[0]!.type),
                      )?.note
                    }
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
